/**
 * The reflowing scheduler — a pure port of the reference app's recompute()
 * (reference.html lines 2012–2093), split from its DOM writes. Consumers
 * (UI, route map, tests) read the returned computation instead of the DOM.
 */
import { isFixed, type DayDef, type DayEnds, type FixDurs, type SlotItem, type Slots, type SlotValue, type TravelInfo, type TripDefinition } from "../types.js";
import { fmin, pmin } from "./time.js";
import { geoOf, travelMin, travelTo, type LatLng } from "./travel.js";

/**
 * How much worse an option is than the closest one on the list.
 *  standard — within 15 min of the best; no reason to call it out
 *  far      — 15–30 min further than the best (flag amber)
 *  veryfar  — more than 30 min further (flag red)
 */
export type DetourTier = "standard" | "far" | "veryfar";

export const DETOUR_FAR = 15;
export const DETOUR_VERYFAR = 30;

export function detourTier(deltaMin: number): DetourTier {
  if (deltaMin > DETOUR_VERYFAR) return "veryfar";
  if (deltaMin >= DETOUR_FAR) return "far";
  return "standard";
}

export interface OptionCost {
  actId: string;
  /** travel minutes from the previous stop; null = not comparable (nomodal or no geo/boat) */
  cost: number | null;
  /** extra minutes versus the closest option on this list */
  delta: number | null;
  tier: DetourTier;
}

export interface FixedRowComp {
  kind: "fixed";
  i: number;
  /** pinned start (minutes; may exceed 1440 past midnight) */
  tMin: number;
  eMin: number | null;
  /** minutes, set when a user override replaced the item's own t/e — the
   * duration actually used, so renderers know to show the effective end */
  ovMin?: number;
}

export interface SlotRowComp {
  kind: "slot";
  /** item index — or "x1", "x2"… for extra slots appended beyond the definition */
  i: number | string;
  /** dur === 0 — row renders "—", travel chains across it */
  skipped: boolean;
  startMin: number;
  endMin: number;
  travel: TravelInfo;
  /** ends past dayEnd → tint red */
  late: boolean;
  /** dropdown annotations, in opts order (absent when skipped, matching the reference) */
  optionCosts: OptionCost[] | null;
  /** where this leg started, for the route map */
  fromGeo: LatLng | null;
  geo: LatLng | null;
  /** set when this stop's clock differs from the previous located stop's */
  tzChange?: { from: string; to: string; deltaMin: number } | null;
}

export type RowComp = FixedRowComp | SlotRowComp;

/** What the day could still hold after every pick is placed. */
export interface ExtraSlotInfo {
  /** item index the extra rows render after (the day's last flexible slot) */
  afterIndex: number;
  /** extra keys that currently hold state, in order: d13_x1, d13_x2 … */
  keys: string[];
  /** minutes of unused time between the last pick and the next fixed anchor (or the day-end target) */
  slackMin: number;
  /** the union of the day's slot options — what an extra slot may offer */
  opts: string[];
  /** travel minutes per option from wherever the last pick leaves you — what
   * the not-yet-filled "add an activity" dropdown shows */
  optionCosts: OptionCost[];
  /** "6:00 PM" style label for what the slack runs up against, null = day-end target */
  anchorLabel: string | null;
}

export interface DayComputation {
  dayId: string;
  /** rows[0..items.length-1] align with day.items; extra-slot rows follow */
  rows: (RowComp | null)[];
  /** null when the day has no home or is a noEnd day */
  backAtBase: { min: number; travel: TravelInfo; late: boolean } | null;
  /** deadline (dl) overrun message, else late-return suggestion; deadline wins */
  warning: string | null;
  warningKind: "deadline" | "late" | null;
  /** null when the day has no flexible slots */
  extra: ExtraSlotInfo | null;
}

const slotKey = (dayId: string, i: number | string) => `${dayId}_${i}`;

/** Extra slots a day may grow, beyond the definition's own. */
export const MAX_EXTRA_SLOTS = 6;
/** Don't offer another slot for less than this much unused time. */
export const EXTRA_SLOT_MIN = 60;

/** IANA timezone of an activity, via its pin's region. */
export function actTz(def: TripDefinition, actId: string): string | null {
  const g = def.geo[actId];
  return (g && def.tz?.[g[2]]) || null;
}

const tzOffsetCache = new Map<string, number>();

/**
 * UTC offset in minutes. What travelers care about is the clock moving, not
 * the zone id — Amsterdam and Zagreb are different zones with the same clock,
 * and warning on that pair would teach people to ignore the warning.
 */
export function tzOffsetMin(tz: string, at: Date = new Date()): number {
  const key = `${tz}@${at.toDateString()}`;
  const hit = tzOffsetCache.get(key);
  if (hit !== undefined) return hit;
  let off = 0;
  try {
    const part = new Intl.DateTimeFormat("en-US", { timeZone: tz, timeZoneName: "longOffset" })
      .formatToParts(at)
      .find((p) => p.type === "timeZoneName")?.value; // "GMT+02:00"
    const m = part?.match(/GMT([+-])(\d{2}):(\d{2})/);
    if (m) off = (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3]));
  } catch {
    /* unknown zone id — treat as UTC, which disables rather than invents warnings */
  }
  tzOffsetCache.set(key, off);
  return off;
}

/** Minutes past the day's target before the plan is flagged as running late. */
export const LATE_GRACE = 45;

/** Pure core of recompute() for one day. */
export function computeDay(def: TripDefinition, day: DayDef, slots: Slots, dayEndMin?: number, fixDurs: FixDurs = {}): DayComputation {
  const dayEnd = dayEndMin ?? def.dayEnd;
  const grace = def.lateGrace ?? LATE_GRACE;
  const home = def.dayHome[day.id];
  let prevGeo: LatLng | null = home ? def.hotelPts[home[0]] ?? null : null;
  let prevAct: string | null = null;
  let cur = pmin(day.start);
  let warnMsg = "";
  let lastFlexLabel = "";
  const rows: (RowComp | null)[] = [];

  /* the representative instant for timezone offsets; exact hour is irrelevant,
     the DST season is what matters */
  const tzDate = day.iso ? new Date(`${day.iso}T12:00:00Z`) : new Date();
  let prevTz: string | null = null;

  /** One flexible slot — the same math whether it comes from the definition or
   * is an extra the user grew the day by. Extras are computed at their true
   * position in time but buffered into `sink`, because rows[0..items.length-1]
   * must stay aligned with day.items for every consumer that indexes by item. */
  const processSlot = (i: number | string, st: SlotValue | undefined, opts: string[], itemMin?: string, sink: (RowComp | null)[] = rows): void => {
    if (!st || st.dur === 0) {
      sink.push({
        kind: "slot", i, skipped: true, startMin: 0, endMin: 0,
        travel: { min: 0, icon: "" }, late: false, optionCosts: null, fromGeo: prevGeo, geo: null,
      });
      return;
    }
    const a = def.act[st.act];
    const g = geoOf(def, st.act);
    const tv = travelTo(def, st.act, prevGeo, prevAct);

    /* travel annotation per option: absolute minutes from wherever the
       previous stop leaves you */
    const costs: OptionCost[] = opts.map((o) => {
      const oa = def.act[o];
      if (!oa || oa.nomodal || (!geoOf(def, o) && !def.boat[o]))
        return { actId: o, cost: null, delta: null, tier: "standard" as DetourTier };
      return { actId: o, cost: travelTo(def, o, prevGeo, prevAct).min, delta: null, tier: "standard" as DetourTier };
    });
    const real = costs.filter((x) => x.cost !== null);
    const minC = real.length > 1 ? Math.min(...real.map((x) => x.cost!)) : null;
    for (const x of costs) {
      if (x.cost === null || minC === null) continue;
      x.delta = x.cost - minC;
      x.tier = detourTier(x.delta);
    }

    let s = cur + tv.min;
    if (a.fs) s = Math.max(pmin(a.fs), cur + tv.min);
    if (itemMin) s = Math.max(s, pmin(itemMin));
    const e = s + Math.round(st.dur * 60);

    /* clock-shift warning: only when both stops carry a zone AND the zones
       actually disagree on the time of day */
    let tzChange: SlotRowComp["tzChange"] = null;
    const tz = actTz(def, st.act);
    if (tz && prevTz && tz !== prevTz) {
      const deltaMin = tzOffsetMin(tz, tzDate) - tzOffsetMin(prevTz, tzDate);
      if (deltaMin !== 0) tzChange = { from: prevTz, to: tz, deltaMin };
    }

    sink.push({
      kind: "slot", i, skipped: false, startMin: s, endMin: e, travel: tv,
      late: e > dayEnd, optionCosts: costs, fromGeo: prevGeo, geo: g, tzChange,
    });
    cur = e;
    if (g) prevGeo = g;
    if (tz) prevTz = tz;
    if (a && !a.nomodal) {
      lastFlexLabel = typeof i === "number" ? (day.items[i] as SlotItem).s : "Extra time";
      prevAct = st.act;
    }
  };

  /** Extra slots live after the last flexible slot the definition declares. */
  const lastFlexIdx = day.items.reduce((acc, it, i) => (isFixed(it) ? acc : i), -1);
  const extraKeys: string[] = [];
  const extraRows: (RowComp | null)[] = [];
  let curAfterExtras = -1;
  let extraFromGeo: LatLng | null = null;
  let extraFromAct: string | null = null;

  day.items.forEach((it, i) => {
    if (isFixed(it)) {
      let t = pmin(it.t);
      if (i > 0 && t < cur - 600) t += 1440; /* past-midnight items */
      if (it.dl && cur > t && !warnMsg) warnMsg = it.dlmsg || "";
      const ov = fixDurs[slotKey(day.id, i)];
      // flights run on their published schedule — an override on one is stale
      // state from before the UI stopped offering it, never timing truth
      const isFlightRow = (it.ic + it.l).includes("✈");
      const hasOv = !isFlightRow && typeof ov === "number" && ov > 0;
      const eMin = hasOv ? t + ov : it.e ? pmin(it.e) : null;
      rows.push({ kind: "fixed", i, tMin: t, eMin, ovMin: hasOv ? ov : undefined });
      if (hasOv) {
        cur = t + ov; // t + ov is always >= t, so no past-midnight wrap needed
      } else {
        cur = it.e ? pmin(it.e) : Math.max(cur, t);
        if (it.e && pmin(it.e) < t) cur = pmin(it.e) + 1440;
      }
      if (it.at && def.hotelPts[it.at]) {
        prevGeo = def.hotelPts[it.at];
        prevAct = null;
      }
      return;
    }

    processSlot(i, slots[slotKey(day.id, i)], it.opts, it.min);

    /* grow the day here: extra picks run in sequence between the definition's
       last flexible slot and whatever fixed anchor follows it */
    if (i === lastFlexIdx) {
      for (let n = 1; n <= MAX_EXTRA_SLOTS; n++) {
        const key = slotKey(day.id, `x${n}`);
        const st = slots[key];
        if (!st) break; // extras are contiguous: x2 can't exist without x1
        extraKeys.push(key);
        processSlot(`x${n}`, st, extraOpts(day), undefined, extraRows);
      }
      curAfterExtras = cur;
      extraFromGeo = prevGeo;
      extraFromAct = prevAct;
    }
  });

  /* back at base */
  let backAtBase: DayComputation["backAtBase"] = null;
  if (home && !def.noEnd[day.id]) {
    const tv = prevAct && def.boat[prevAct] ? def.boat[prevAct] : travelMin(prevGeo, def.hotelPts[home[1]] ?? null);
    const backT = cur + tv.min;
    backAtBase = { min: backT, travel: tv, late: backT > dayEnd + grace };
  }

  let warning: string | null = null;
  let warningKind: DayComputation["warningKind"] = null;
  if (warnMsg) {
    warning = warnMsg;
    warningKind = "deadline";
  } else if (backAtBase && backAtBase.late) {
    const target = fmin(dayEnd).replace(/:00\s/, " "); // "10:00 PM" -> "10 PM"
    warning =
      "This plan gets you back around " + fmin(backAtBase.min) + " — more than " + grace +
      " min past the " + target + " target. Use “Fit to target”, shorten a time block, or set a later slot" +
      (lastFlexLabel ? " (e.g. “" + lastFlexLabel + "”)" : "") +
      " to “Skip”.";
    warningKind = "late";
  }

  rows.push(...extraRows);

  /* how much of the day is still unspoken for, and where it runs out */
  let extra: ExtraSlotInfo | null = null;
  if (lastFlexIdx >= 0) {
    let anchorMin: number | null = null;
    let anchorLabel: string | null = null;
    for (let j = lastFlexIdx + 1; j < day.items.length; j++) {
      const it = day.items[j];
      if (isFixed(it) && it.t) {
        anchorMin = pmin(it.t);
        if (anchorMin < curAfterExtras - 600) anchorMin += 1440;
        anchorLabel = fmin(anchorMin);
        break;
      }
    }
    if (anchorMin === null) anchorMin = dayEnd;
    const opts = extraOpts(day);
    extra = {
      afterIndex: lastFlexIdx,
      keys: extraKeys,
      slackMin: anchorMin - curAfterExtras,
      opts,
      optionCosts: opts.map((o) => {
        const oa = def.act[o];
        if (!oa || oa.nomodal || (!geoOf(def, o) && !def.boat[o]))
          return { actId: o, cost: null, delta: null, tier: "standard" as DetourTier };
        return { actId: o, cost: travelTo(def, o, extraFromGeo, extraFromAct).min, delta: null, tier: "standard" as DetourTier };
      }),
      anchorLabel,
    };
  }

  return { dayId: day.id, rows, backAtBase, warning, warningKind, extra };
}

/** The union of a day's slot options, in first-appearance order — what an
 * extra slot offers. */
function extraOpts(day: DayDef): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const it of day.items)
    if (!isFixed(it)) for (const o of it.opts) if (!seen.has(o)) { seen.add(o); out.push(o); }
  return out;
}

export interface TripComputation {
  days: Record<string, DayComputation>;
  /** slot keys whose selected activity is chosen in ≥2 slots (trip-wide) */
  duplicates: Set<string>;
}

export function computeTrip(def: TripDefinition, slots: Slots, dayEnds: DayEnds = {}, fixDurs: FixDurs = {}): TripComputation {
  const days: Record<string, DayComputation> = {};
  for (const d of def.days) days[d.id] = computeDay(def, d, slots, dayEnds[d.id], fixDurs);

  const exempt = new Set(def.dupExempt);
  const byAct: Record<string, string[]> = {};
  for (const [k, v] of Object.entries(slots)) {
    if (!exempt.has(v.act) && v.dur !== 0) (byAct[v.act] ??= []).push(k);
  }
  const duplicates = new Set<string>();
  for (const keys of Object.values(byAct)) if (keys.length > 1) for (const k of keys) duplicates.add(k);

  return { days, duplicates };
}

/* ============ fitting a day to its "back at base by" target ============ */

export interface SlotChange {
  key: string;
  value: SlotValue;
}

/**
 * Every alternative duration for a slot, largest first, excluding the current
 * one. Duration 0 (drop the stop) is always available — the scheduler already
 * treats a zero-length slot as skipped and chains travel across it. The chosen
 * activity is never swapped out, so dropping stays reversible: raising the
 * duration again restores the stop.
 */
function durationOptions(def: TripDefinition, day: DayDef, i: number, cur: SlotValue): number[] {
  const item = day.items[i];
  if (isFixed(item)) return [];
  const set = new Set<number>(def.act[cur.act]?.durs ?? []);
  set.add(0);
  return [...set].filter((d) => d !== cur.dur).sort((a, b) => b - a);
}

/**
 * Adjusts a day's flexible slots so the plan lands as close as possible to
 * `target` (back at base) without overshooting it.
 *
 * Greedy hill-climb over the real scheduler: each round tries every legal
 * single-slot duration change (including skipping a stop where the itinerary
 * offers that option) and keeps the one that lands nearest the target. Because
 * every candidate is scored by computeDay, travel time is re-chained for real —
 * dropping a stop re-routes the day rather than just subtracting minutes.
 *
 * 🔒 Booked slots are never touched, and neither are fixed items.
 */
export function fitDayToTarget(def: TripDefinition, day: DayDef, slots: Slots, target: number, fixDurs: FixDurs = {}): SlotChange[] {
  const home = def.dayHome[day.id];
  if (!home || def.noEnd[day.id]) return [];

  const working: Slots = { ...slots };
  const flexible: number[] = [];
  day.items.forEach((it, i) => {
    if (isFixed(it)) return;
    const st = working[slotKey(day.id, i)];
    if (!st || st.st === 2) return; // leave booked commitments alone
    flexible.push(i);
  });
  if (!flexible.length) return [];

  /**
   * Distance from the target, in "penalty minutes": overshooting is much worse
   * than finishing early, and dropping a stop costs a little so the fitter
   * trims time blocks before it removes anything from the day.
   */
  const DROP_COST = 20;
  const score = (s: Slots): number => {
    // fixDurs is threaded through so a candidate is scored against the day the
    // user is actually looking at — an active passport-control override, say,
    // already ate real minutes the fitter must not pretend didn't happen
    const back = computeDay(def, day, s, target, fixDurs).backAtBase;
    if (!back) return Number.POSITIVE_INFINITY;
    const delta = back.min - target;
    const dropped = flexible.filter((i) => s[slotKey(day.id, i)]?.dur === 0).length;
    return (delta > 0 ? delta * 10 : -delta) + dropped * DROP_COST;
  };

  let best = score(working);
  for (let round = 0; round < 24 && best > 0; round++) {
    let bestKey: string | null = null;
    let bestVal: SlotValue | null = null;
    let bestScore = best;

    for (const i of flexible) {
      const key = slotKey(day.id, i);
      const cur = working[key];
      for (const dur of durationOptions(def, day, i, cur)) {
        const candidate: SlotValue = { ...cur, dur };
        const trial = { ...working, [key]: candidate };
        const s = score(trial);
        if (s < bestScore - 0.001) {
          bestScore = s;
          bestKey = key;
          bestVal = candidate;
        }
      }
    }
    if (!bestKey || !bestVal) break; // no single change improves things
    working[bestKey] = bestVal;
    best = bestScore;
  }

  const changes: SlotChange[] = [];
  for (const i of flexible) {
    const key = slotKey(day.id, i);
    const before = slots[key];
    const after = working[key];
    if (before.act !== after.act || before.dur !== after.dur) changes.push({ key, value: after });
  }
  return changes;
}

/** Initial slot state for a definition (reference lines 1820–1822). */
export function seedSlots(def: TripDefinition): Slots {
  const slots: Slots = {};
  for (const d of def.days)
    d.items.forEach((it, i) => {
      if (!isFixed(it)) {
        const a = it.def || it.opts[0];
        slots[slotKey(d.id, i)] = { act: a, dur: it.dd ?? defDur(def, a), st: 0 };
      }
    });
  return slots;
}

/** Default duration: the 2nd option when present (reference line 1824). */
export function defDur(def: TripDefinition, actId: string): number {
  const ds = def.act[actId].durs;
  return ds[Math.min(1, ds.length - 1)] ?? ds[0];
}

/** Merge saved slot values onto a seeded state, dropping stale keys/acts (reference line 1823). */
export function applySavedSlots(def: TripDefinition, seeded: Slots, saved: Slots): Slots {
  const out: Slots = { ...seeded };
  const dayIds = new Set(def.days.map((d) => d.id));
  for (const [k, v] of Object.entries(saved)) {
    if (out[k] && v && def.act[v.act]) out[k] = { act: v.act, dur: v.dur, st: (v.st || 0) as SlotValue["st"] };
    // extra slots aren't seeded — they exist only as state, so a synced key
    // must survive the merge or a co-planner's added stop vanishes on resync
    else if (!out[k] && v && def.act[v.act]) {
      const m = k.match(/^(.+)_x\d+$/);
      if (m && dayIds.has(m[1])) out[k] = { act: v.act, dur: v.dur, st: (v.st || 0) as SlotValue["st"] };
    }
  }
  return out;
}
